import useAuth from 'hooks/useAuth';
import { getTeacherWorkload, deleteTeacherWorkload } from 'services/workload.service';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
  OutlinedInput,
  InputAdornment,
  Chip,
  useTheme
} from '@mui/material';
import moment from 'moment';
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import { AddCircle, Bag, Edit, People, SearchNormal1, CloseCircle } from 'iconsax-react';
import { useLocation, useNavigate } from 'react-router-dom';
import Loader2 from 'components/Loader2';
import { FormattedMessage } from 'react-intl';
import { useEffect, useMemo, useState } from 'react';
import Snackbar from 'utils/Snackbar';
import CustomTable from 'components/@extended/CustomTable';
import SkeletonTable from 'components/SkeletonTable';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { getPlaceholder } from 'utils/getPlaceHolder';
import useConfig from 'hooks/useConfig';
import useContemp from 'hooks/useContemp';
import Avatar from 'components/@extended/Avatar';

function TeacherWorkloadList() {
  const { user }: any = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { i18n } = useConfig();
  const { setData, prevStoredData } = useContemp();
  const theme = useTheme();
  const mode = theme.palette.mode;

  const [workloads, setWorkloads] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [sortingData, setSortingData] = useState<any[]>([]);

  const [openDelete, setOpenDelete] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);

  // Fetch workloads
  const fetchWorkloads = async () => {
    setIsLoading(true);
    try {
      const response = await getTeacherWorkload({
        skip: (pageIndex - 1) * pageSize + 1,
        limit: pageSize,
        search: search.trim()
      });

      if (response.status === 200) {
        const data = response.data?.result;
        const list = data?.teacherWorkloads || [];
        setWorkloads(list);
        setTotalCount(data?.count || 0);
        setNotFound(list.length === 0 && !search);
      } else {
        Snackbar('Failed to load workloads.', 'error');
        setWorkloads([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to load workloads.';
      const type = err?.response?.status === 403 ? 'warning' : 'error';
      Snackbar(message, type);
      setWorkloads([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkloads();
  }, [pageIndex, pageSize, search]);

  useEffect(() => {
    if (prevStoredData?.[pathname]?.pageIndex) {
      setPageIndex(prevStoredData[pathname].pageIndex);
    }
    if (prevStoredData?.[pathname]?.pageSize) {
      setPageSize(prevStoredData[pathname].pageSize);
    }
  }, [prevStoredData, pathname]);

  // Delete workload
  const handleDelete = async () => {
    if (!deletingId) return;

    setDisabled(true);
    try {
      const response = await deleteTeacherWorkload({
        teacherWorkloadId: deletingId
      });

      if (response.status === 200 || response.status === 204) {
        setWorkloads((prev) => prev.filter((w) => w._id !== deletingId));
        setTotalCount((prev) => Math.max(0, prev - 1));
        Snackbar('Workload deleted successfully', 'success');
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to delete workload.';
      Snackbar(message, err?.response?.status === 403 ? 'warning' : 'error');
    } finally {
      setDisabled(false);
      setOpenDelete(false);
      setDeletingId(null);
    }
  };

  const handlePageChange = (event: any, page: number) => {
    setPageIndex(page);
  };

  const handlePageSizeChange = (event: any) => {
    const newSize = event.target.value as number;
    setPageIndex(1);
    setPageSize(newSize);
  };

  const handleSearch = () => {
    if (searchValue.trim()) {
      setPageIndex(1);
      setSearch(searchValue.trim());
    }
  };

  const clearSearch = () => {
    setSearchValue('');
    setSearch('');
    setPageIndex(1);
  };

  // Permissions
  const hasPermission = (action: 'Create' | 'Update' | 'Delete') => {
    return user?.userTypeId?.permission?.TeacherWorkload?.[action] ?? false;
  };

  const canModify = (createdById: string) => {
    if (user?.type === 'admin') return true;
    if (user?.type === 'student') return false;
    if (user?.type === 'faculty') return user?._id === createdById;
    return true;
  };

  const handleSortingChange = (data: any) => {
    const index = sortingData?.findIndex((item: any) => item?.id === data?.name);
    let arr = sortingData || [];

    if (index !== -1 && sortingData[index]?.sort && sortingData[index]?.desc) {
      const arrCopy = [...sortingData];
      arrCopy.splice(index, 1);
      setSortingData(arrCopy);
    } else if (index !== -1 && sortingData[index]?.sort) {
      const newData = { ...sortingData[index], desc: true };
      const newArr = [...arr];
      newArr[index] = newData;
      setSortingData(newArr);
    } else {
      arr = [...arr, { id: data.name, sort: true, desc: false }];
      setSortingData(arr);
    }
  };

  const sortArray = ['teacherId.firstName', 'divisionId.divisionName', 'subjectId.subjectName'];

  const columns = useMemo(
    () => [
      {
        Header: 'Teacher',
        accessor: 'teacherId.firstName',
        Cell: ({ row }: any) => {
          const teacher = row.original.teacherId;
          const name = teacher ? `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() || '--' : '--';
          return <Typography>{name}</Typography>;
        }
      },
      {
        Header: 'Division',
        accessor: 'divisionId.divisionName',
        Cell: ({ row }: any) => (
          <Typography >{row.original.divisionId?.divisionName || '--'}</Typography>
        )
      },
      {
        Header: 'Subject',
        accessor: 'subjectId.subjectName',
        Cell: ({ row }: any) => (
          <Typography>{row.original.subjectId?.subjectName || '--'}</Typography>
        )
      },
      {
        Header: 'Max Lectures',
        accessor: 'maxLectures',
        Cell: ({ value }: any) => (
          <Typography>
            {value ?? '--'}
          </Typography>
        )
      },
      {
        Header: 'Created By',
        accessor: 'createdBy',
        Cell: ({ row, value }: any) => {
          const name = value ? `${value.firstName || ''} ${value.lastName || ''}`.trim() : '--';
          const profilePic = value?.userProfilePicId;
          const avatarSrc = profilePic?.path
            ? `${process.env.REACT_APP_FILE_URL}${profilePic.path}`
            : profilePic?.mimetype && profilePic?.file
            ? `data:${profilePic.mimetype};base64,${profilePic.file}`
            : '';

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column'}}>
              {value ? (
                <Tooltip
                  title={
                    <>
                      {moment(row.original.created_at).format('DD/MM/YYYY, HH:mm')}
                      <br />
                      {name}
                    </>
                  }
                >
                  <span>
                    <Avatar alt={value.firstName} size="sm" src={avatarSrc} sx={{ my: 1 }} />
                  </span>
                </Tooltip>
              ) : (
                '--'
              )}
            </Box>
          );
        }
      },
      {
        Header: 'Last Updated By',
        accessor: 'updatedBy',
        Cell: ({ row, value }: any) => {
          const lastUpdate = value?.length > 0 ? value[0] : null;
          const name = lastUpdate ? `${lastUpdate.firstName || ''} ${lastUpdate.lastName || ''}`.trim() : '--';
          const profilePic = lastUpdate?.userProfilePicId;
          const avatarSrc = profilePic?.path
            ? `${process.env.REACT_APP_FILE_URL}${profilePic.path}`
            : profilePic?.mimetype && profilePic?.file
            ? `data:${profilePic.mimetype};base64,${profilePic.file}`
            : '';

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column'}}>
              {lastUpdate ? (
                <Tooltip
                  title={
                    <>
                      {moment(row.original.updated_at).format('DD/MM/YYYY, HH:mm')}
                      <br />
                      {name}
                    </>
                  }
                >
                  <span>
                    <Avatar alt={lastUpdate.firstName} size="sm" src={avatarSrc} sx={{ my: 1 }} />
                  </span>
                </Tooltip>
              ) : (
                '--'
              )}
            </Box>
          );
        }
      },
      {
        Header: 'Options',
        accessor: '_id',
        disableSortBy: true,
        Cell: ({ row }: any) => {
          const createdById = row.original.createdBy?._id;


          return (
            <Stack direction="row" spacing={1}>
              <Tooltip title={<FormattedMessage id="Edit" />}>
                <span>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => {
                      setData({ [pathname]: { pageIndex, pageSize } });
                      navigate('/teacherworkload/add', { state: { rowData: row.original } });
                    }}
                  >
                    <Edit size={18} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={<FormattedMessage id="Delete" />}>
                <span>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => {
                      setDeletingId(row.original._id);
                      setOpenDelete(true);
                    }}
                  >
                    <Bag size={18} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        }
      }
    ],
    [pathname, pageIndex, pageSize, navigate, setData, hasPermission]
  );

  return (
    <>
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDelete} onClose={() => !disabled && setOpenDelete(false)}>
        <Box sx={{ p: 1, pb: 1.2, textAlign: 'center' }}>
          <DialogTitle sx={{ fontSize: '20px' }}>
            <b>Delete Workload</b>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>Are you sure you want to delete this workload?</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => setOpenDelete(false)} disabled={disabled}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDelete}
              disabled={disabled}
              sx={{ ':hover': { bgcolor: '#dc2626', color: 'white' } }}
            >
              Delete
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <MainCard
        title={
          <Box display="flex" alignItems="center">
            <BackToggle show />
            <Box ml={2}>
            <Typography variant="h5">
              <FormattedMessage id="Teacher Workload List" />
            </Typography>
            <Typography variant="body2">
              <FormattedMessage id="Manage workload details with options to add, edit, or delete." />
            </Typography>
          </Box>
          </Box>
        }
        content={false}
        secondary={
          <Stack direction="row-reverse" spacing={2} alignItems="center">
            <Tooltip title={<FormattedMessage id="Add workload" />}>
              <span>
                <IconButton
                  variant="dashed"
                  color="primary"
                  onClick={() => navigate('/teacherworkload/add', { state: { rowData: {} } })}
                >
                  <AddCircle />
                </IconButton>
              </span>
            </Tooltip>

            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={<FormattedMessage id="Search" />}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSearch}
                  disabled={!searchValue.trim()}
                  sx={{ p: '9px', minWidth: 'auto' }}
                >
                  <SearchNormal1 />
                </Button>
              </Tooltip>
              <OutlinedInput
                value={searchValue}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchValue(val);
                  if (!val.trim()) clearSearch();
                }}
                onKeyDown={(e) => e.key === 'Enter' && searchValue.trim() && handleSearch()}
                placeholder={getPlaceholder(i18n, totalCount)}
                endAdornment={
                  searchValue ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <CloseCircle fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              />
            </Stack>
          </Stack>
        }
      >
        {/* Search Chip Display */}
        {search && (
          <Stack sx={{ my: 1, mx: 1 }} direction="row" flexWrap="wrap" gap={1}>
            <Box
              sx={{
                border: '1px solid gray',
                borderRadius: '15px',
                px: 1.5,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                bgcolor: mode === 'dark' ? 'background.paper' : '#F8F9FA'
              }}
            >
              <Typography variant="body2" sx={{ mr: 1 }}>
                <b>Search:</b>
              </Typography>
              <Chip label={search} onDelete={clearSearch} color="primary" />
            </Box>
          </Stack>
        )}

        {/* Loader Overlay */}
        {disabled && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
              bgcolor: 'rgba(0,0,0,0.05)'
            }}
          >
            <Loader2 />
          </Box>
        )}

        {isLoading ? (
          <SkeletonTable columns={columns} />
        ) : (
          <Box sx={{ height: '100%', overflow: 'hidden', filter: disabled ? 'grayscale(100%) blur(3px)' : 'none' }}>
            <CustomTable
              icon={People}
              text={<FormattedMessage id="No workload available, Add workload now !" />}
              link="/teacherworkload/add"
              columns={columns}
              data={workloads || []}
              count={totalCount}
              size={pageSize}
              pageIndex={pageIndex}
              handlePageChange={handlePageChange}
              handleSizeChange={handlePageSizeChange}
              notFound={notFound}
              search={search}
              sortArray={sortArray}
              sort={sortingData}
              setSortingData={handleSortingChange}
            />
          </Box>
        )}
      </MainCard>
    </>
  );
}

export default TeacherWorkloadList;